<template>
  <div class="ti-page-start py2">
    <div class="lg-section px3">
      <router-link :to="{name: 'connect-list'}" class="ti-link">🡸 Vendor Connect</router-link>
    </div>
    <div v-if="!loading && loadError" class="lg-section px2 pt2">
      <p>{{loadError}}</p>
    </div>
    <template v-else-if="!loading">
      <div class="lg-section px3 flex">
        <h2 class="detail-header-title flex-auto">
          Vendor Connect: {{vendor.name}}
          <span v-if="isPreview" style="color:red; font-size: x-large;">PREVIEW</span>
        </h2>
        <div v-if="vendor.connectLogo" style="text-align:right;">
          <img :src="vendor.connectLogo" :alt="vendor.name"
               style="width:100px;height:auto; margin:3px;"/>
        </div>
      </div>

      <div v-if="canAccess" class="lg-section flex px2 pt3">
        <div class="sm-col sm-col-12 md-col-4 mr3">
          <template v-if="show=='all'">
            <button v-if="isPreview" @click="show='dry'">Condensed View</button>
          </template>
          <button v-else @click="show='all'">Full Detail</button>
          <TreeFilter allLabel="All Sectors" headerClass="ti-tag-sector"
                      :listItems="$store.state.refMap.vertical_items"
                      :items="$store.state.refMap.verticals"
                      :filters="$store.state.verticals"
                      :counts="capabilities.countVert"
                      :showCount="false"
                      @itemClick="toggleVerticalItem"
                      class="mt2"/>
          <TreeFilter allLabel="All Technologies" headerClass="ti-tag-technology"
                      :listItems="this.$store.getters.getTechKeys"
                      :items="$store.state.refMap.tech_areas"
                      :show="$store.state.refMap.connectTech"
                      :filters="$store.state.techs"
                      :counts="capabilities.countTech"
                      :showCount="false"
                      @itemClick="toggleTechItem"
                      class="mt2"/>
        </div>

        <div class="sm-col sm-col-12 md-col-8">
          <div v-if="capabilities.items.length===0" class="m3">
            No matching capabilities.
          </div>
          <template v-else v-for="cap in capabilities.items">
            <CapabilityCard :key="cap.id"
                            :vendorId="objId"
                            :vendorName="vendor.name"
                            :preview="isPreview"
                            :draft="cap.draft"
                            :enquirer="enquirer"
                            :contacts="cap.contacts"
                            :tech="cap.tech" :vertical="cap.vertical"
                            :attrs="cap.props" class="mt2"/>
          </template>
        </div>
      </div>
      <div v-else class="lg-section px2 pt3"> <!-- !canAccess -->
        <div class="ml3 mb2">
          <div v-if="error">
            <span class="ti-message ti-message-alert">
              {{error}}
            </span>
          </div>
          <div v-else>
            <span class="ti-message ti-message-alert">
              You currently do not have permission to access this page.
            </span>
            <p class="ml3">
              <a href="/login" class="ti-link">Log in</a> or
              <a href="/signup" class="ti-link">sign up</a>
              to a free CONNECT subscription to access.
            </p>
          </div>
        </div>
      </div>
    </template> <!-- if loaded -->
  </div>
</template>

<script>
import axios from 'axios';
import TreeFilter from '@/components/TreeFilter.vue';
import CapabilityCard from './CapabilityCard.vue';


export default {
  name: 'ConnectDetail',
  components: { TreeFilter, CapabilityCard },
  data() {
    return {
      loading: true, // first load of refMap
      canAccess: null,
      error: null,
      loadError: null,
      objId: this.$route.params.id,
      vendor: null,
      isPreview: Boolean(this.$route.query.preview),
      enquirer: null, // {name, email} used on enquire form

      show: 'all', // all | dry
    };
  },

  computed: {
    allTechs() {
      return !Object.values(this.$store.state.techs).some((x) => x);
    },
    allVerts() {
      return !Object.values(this.$store.state.verticals).some((x) => x);
    },
    // contains list of index of vendor capabilities to be displayed
    capabilities() {
      const result = [];
      let countVert = null;
      let countTech = null;
      if (this.show === 'dry') {
        const scores = []; // array-index => score
        const caps = this.vendor.capDry;
        // display value only above benchmark, this is set to genric capability score.
        // Only used if any filter applied.
        let benchmark = 0;
        for (let i = 0; i < caps.length; i++) {
          const cap = caps[i];
          const score = this.capDisplayScore(cap);
          scores.push([i, score]); // , cap.tech, cap.vertical]);
          if (!(this.allVerts && this.allTechs) // should set a benchmark
              && (cap.tech === null && cap.vertical === null)) { // is generic cap
            benchmark = score;
          }
        }

        scores.sort((a, b) => b[1] - a[1]); // descending order
        for (let i = 0; i < scores.length; i++) {
          if (scores[i][1] > benchmark) {
            result.push(caps[scores[i][0]]);
          }
        }

        // show general cap if result would be empty
        if (result.length === 0) {
          result.push(caps[scores[0][0]]);
        }
      // simple filter - full view
      } else {
        countVert = {};
        countTech = {};
        const caps = this.vendor.capAll;
        for (let i = 0; i < caps.length; i++) {
          if (this.capDisplayFilter(caps[i])) {
            result.push(caps[i]);
            if (caps[i].tech in countTech) {
              countTech[caps[i].tech] += 1;
            } else {
              countTech[caps[i].tech] = 1;
            }
            if (caps[i].vertical in countVert) {
              countVert[caps[i].vertical] += 1;
            } else {
              countVert[caps[i].vertical] = 1;
            }
          }
        }
      }
      return {
        countTech,
        countVert,
        items: result,
      };
    },
  },

  created() {
    const params = {};
    if (this.$store.state.refMap === null) {
      // if page loaded from URL
      params.schema = '1';
    }
    if (this.isPreview) {
      params.preview = '1';
      params.token = this.$route.query.token;
    }
    axios.get(`/api/connect/${this.objId}`, { params })
      .then((res) => {
        if (res.data.result === 'fail') {
          this.loadError = res.data.message;
        } else {
          this.vendor = res.data.vendor;
          this.canAccess = res.data.canAccess;
          this.error = res.data.error;
          if (this.canAccess) {
            this.enquirer = res.data.enquirer;
            if (this.$store.state.refMap === null) {
              this.$store.commit('initMaps', res.data.refMap);
            }
          }
        }
        this.loading = false;
      });
  },

  methods: {
    toggleVerticalItem(code) {
      let payload;
      if (code === null) {
        payload = { property: 'verticals', val: null };
      } else {
        const current = Boolean(this.$store.state.verticals[code]);
        payload = { property: 'verticals', id: code, val: !current };
      }
      this.$store.commit('setFilter', payload);
    },

    toggleTechItem(param) {
      let payload;
      if (param === null) {
        payload = { property: 'techs', val: null };
      } else {
        const current = Boolean(this.$store.state.techs[param]);
        payload = { property: 'techs', id: param, val: !current };
      }
      this.$store.commit('setFilter', payload);
    },

    /* DRY moded - compute a score to determine if a card should be displayed and its order */
    capDisplayScore(cap) {
      let score = 0;
      if (this.allVerts) {
        if (cap.vertical === null) {
          score += 2; // BOTH is ALL
        } else {
          score += 1; // filter is ALL, match specific
        }
      } else {
        if (cap.vertical === null) {
          score += 1; // item is ALL, match whatever filter
        } else if (this.$store.state.verticals[cap.vertical] === true) {
          score += 3; // item SPECIFIC matches a filter
        } else {
          return 0; // not a match
        }
      }

      if (this.allTechs) {
        if (cap.tech === null) {
          score += 2;
        } else {
          score += 1;
        }
      } else {
        if (cap.tech === null) {
          score += 1;
        } else if (this.$store.state.techs[cap.tech] === true) {
          score += 3;
        } else {
          return 0;
        }
      }

      return score;
    },

    /* ALL mode - return bool if card matches filter */
    capDisplayFilter(cap) {
      if (!this.allVerts) {
        if (this.$store.state.verticals[cap.vertical] !== true) {
          return false; // not a match
        }
      }

      if (!this.allTechs) {
        if (this.$store.state.techs[cap.tech] !== true) {
          return false;
        }
      }

      return true;
    },
  },
};
</script>


<style>
</style>
